import { createStore } from "./createStore";

export interface BrowserHistory {
  entries: string[];
  index: number;
}

export interface BrowserState {
  // Dock visibility. Lives on the singleton store (not WorkbenchView local
  // state) so it survives WorkbenchView remounts within the same JS session —
  // route switches, Tauri webview reopen with the process alive, StrictMode
  // double-mount. Mirrors terminalsStore.paneVisible.
  paneVisible: boolean;
  // Current address-bar value (also the source for the iframe src).
  address: string;
  // Per-pane navigation stack (store-backed back/forward).
  history: BrowserHistory;
  // The project the pane resolves relative paths / the dev server against.
  projectId: string | null;
}

// Exported so tests target the exact key the store writes to.
export const BROWSER_PANE_VISIBLE_KEY = "agentguard-browser-pane-visible";

function readPersistedPaneVisible(): boolean {
  try {
    return localStorage.getItem(BROWSER_PANE_VISIBLE_KEY) === "true";
  } catch {
    return false;
  }
}

export const browserStore = createStore<BrowserState>({
  paneVisible: readPersistedPaneVisible(),
  address: "",
  history: { entries: [], index: -1 },
  projectId: null,
});

// Persist paneVisible via a subscriber so setState stays a pure reducer
// (mirrors terminalsStore). Reference check skips unrelated updates.
let lastPersistedPaneVisible = readPersistedPaneVisible();
browserStore.subscribe(() => {
  const current = browserStore.getSnapshot().paneVisible;
  if (current === lastPersistedPaneVisible) return;
  lastPersistedPaneVisible = current;
  try {
    localStorage.setItem(BROWSER_PANE_VISIBLE_KEY, current ? "true" : "false");
  } catch {
    // hardened webviews may block localStorage; in-session selection still works.
  }
});

export function setBrowserPaneVisible(visible: boolean): void {
  browserStore.setState({ paneVisible: visible });
}

// Navigate to a new address. Truncates any forward history (standard browser
// semantics), de-dupes a consecutive identical address, and binds projectId
// when provided (undefined = keep current binding).
export function navigateBrowser(address: string, projectId?: string | null): void {
  browserStore.setState((s) => {
    const entries = s.history.entries.slice(0, s.history.index + 1);
    // Dedup by ADDRESS only: re-navigating to the same address skips a new
    // history entry, but the projectId rebind below still applies.
    if (entries[entries.length - 1] !== address) entries.push(address);
    return {
      ...s,
      address,
      projectId: projectId !== undefined ? projectId : s.projectId,
      history: { entries, index: entries.length - 1 },
    };
  });
}

export function browserGoBack(): void {
  browserStore.setState((s) => {
    if (s.history.index <= 0) return s;
    const index = s.history.index - 1;
    return { ...s, history: { ...s.history, index }, address: s.history.entries[index] };
  });
}

export function browserGoForward(): void {
  browserStore.setState((s) => {
    if (s.history.index >= s.history.entries.length - 1) return s;
    const index = s.history.index + 1;
    return { ...s, history: { ...s.history, index }, address: s.history.entries[index] };
  });
}

export type BrowserSrc =
  | { kind: "iframe"; src: string; external?: boolean; nativeSrc?: string }
  | { kind: "empty"; reason: string };

// Pure mapping from an address-bar value to a same-origin URL the iframe can
// render. Supports project files (/api/preview/file), the project's localhost
// dev server (/api/preview/dev), and public http(s) URLs routed through the
// SSRF proxy (/api/browser/proxy) with external:true. File/dev resolution
// requires a bound project; external proxy does not.
export function resolveBrowserSrc(address: string, projectId: string | null): BrowserSrc {
  const a = address.trim();
  if (!a) return { kind: "empty", reason: "Enter an address to start browsing." };

  // http(s):// — public web vs localhost dev server. Parse with URL so the
  // path, query, and hash are split correctly (a raw regex on the host swallows
  // "?query"); hostname also excludes the port, so the local check can't be
  // fooled by "localhost.evil.com".
  if (/^https?:\/\//i.test(a)) {
    let url: URL;
    try {
      url = new URL(a);
    } catch {
      return { kind: "empty", reason: "Invalid URL format." };
    }
    const hostname = url.hostname.toLowerCase();
    const isLocal =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "0.0.0.0" ||
      hostname === "[::1]";
    const nativeSrc = hostname === "0.0.0.0" ? a.replace("0.0.0.0", "127.0.0.1") : a;
    if (!isLocal) {
      // Keep the #fragment outside the encoded url param so the opaque-origin
      // iframe still gets a real client-side fragment (anchor scroll / hash
      // routing) rather than an opaque %23 buried in the query string.
      const proxied = url.hash
        ? `/api/browser/proxy?url=${encodeURIComponent(a.slice(0, a.indexOf("#")))}${url.hash}`
        : `/api/browser/proxy?url=${encodeURIComponent(a)}`;
      return { kind: "iframe", src: proxied, external: true, nativeSrc: a };
    }
    if (!projectId) return { kind: "iframe", src: nativeSrc, external: true, nativeSrc };
    return {
      kind: "iframe",
      src: `/api/preview/dev/${encodeURIComponent(projectId)}${url.pathname}${url.search}${url.hash}`,
      nativeSrc,
    };
  }

  // Bare host:port (no scheme), e.g. "localhost:5173/x?y#z" → dev proxy. The
  // suffix may start with "/", "?", or "#"; a query/hash-only suffix gets the
  // leading "/" the bare host omits so the proxy still sees a rooted path.
  const hostPortMatch = a.match(/^(localhost|127\.0\.0\.1|0\.0\.0\.0)(?::\d+)?([/?#].*)?$/i);
  if (hostPortMatch) {
    const host = hostPortMatch[1].toLowerCase() === "0.0.0.0" ? "127.0.0.1" : hostPortMatch[1];
    const suffix = hostPortMatch[2] || "/";
    const path = suffix.startsWith("/") ? suffix : `/${suffix}`;
    const portMatch = a.match(/^(?:localhost|127\.0\.0\.1|0\.0\.0\.0)(:\d+)?/i);
    const nativeSrc = `http://${host}${portMatch?.[1] || ""}${path}`;
    if (!projectId) return { kind: "iframe", src: nativeSrc, external: true, nativeSrc };
    return {
      kind: "iframe",
      src: `/api/preview/dev/${encodeURIComponent(projectId)}${path}`,
      nativeSrc,
    };
  }

  // Otherwise: a project-relative file path, which may carry a ?query / #hash.
  // Only the path segments are percent-encoded; the query/hash pass through raw
  // (encoding "?"/"=" into the path is what broke the server route before).
  if (!projectId) return { kind: "empty", reason: "Open a project to preview files." };
  const rel = a.replace(/^\/+/, "");
  const qh = rel.search(/[?#]/);
  const filePath = qh === -1 ? rel : rel.slice(0, qh);
  const suffix = qh === -1 ? "" : rel.slice(qh);
  const encoded = filePath.split("/").map(encodeURIComponent).join("/");
  return {
    kind: "iframe",
    src: `/api/preview/file/${encodeURIComponent(projectId)}/${encoded}${suffix}`,
  };
}
